import { useState, useEffect } from "react"
import { Globe, Plus, X, Save, Loader2, AlertCircle, Languages, FileText, Trash2, CheckCircle, Copy } from "lucide-react"
import { Button } from "../ui/button"
import TipTapEditor from "../TipTapEditor"
import { api } from "../../utils/apiComponent/apiClient"
import { endpoints } from "../../utils/endpoints"
import { showSuccessToast, showErrorToast } from "../../utils/toast"

// Langues supportées
const SUPPORTED_LANGUAGES = [
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'en', name: 'English', flag: '🇬🇧' }
]

// Types de questions pour les quiz
const QUESTION_TYPES = [
  { value: 'SINGLE_CHOICE', label: 'Choix unique' },
  { value: 'MULTIPLE_CHOICE', label: 'Choix multiple' },
  { value: 'TEXT', label: 'Réponse textuelle' },
  { value: 'TRUE_FALSE', label: 'Vrai/Faux' },
  { value: 'ESSAY', label: 'Essai' },
  { value: 'NUMERIC', label: 'Réponse numérique' },
  { value: 'FILL_IN_THE_BLANK', label: 'Remplir les blancs' },
  { value: 'MATCHING', label: 'Association' }
]

export default function TranslationManager({
  type, // 'course', 'module', 'lesson', 'block', 'quiz'
  entityId,
  entityData, // Les données de l'entité (pour pré-remplir les champs)
  initialLanguage, // Langue à ouvrir directement (optionnel)
  onClose,
  onTranslationSaved,
  onLanguageChange, // Callback appelé quand la langue change (pour recharger l'entité traduite)
  hideHeader = false // Masquer le header si intégré dans un autre formulaire
}) {
  const [availableLanguages, setAvailableLanguages] = useState([])
  const [translations, setTranslations] = useState({})
  const [loading, setLoading] = useState(true)
  const [loadingLanguage, setLoadingLanguage] = useState(false)
  const [saving, setSaving] = useState(false)
  const [selectedLanguage, setSelectedLanguage] = useState(null)
  const [translationData, setTranslationData] = useState({})
  const [errors, setErrors] = useState({})
  
  // Pour les champs de type array (requirements, objectives)
  const [newRequirement, setNewRequirement] = useState('')
  const [newObjective, setNewObjective] = useState('')
  
  // Pour les questions de quiz
  const [questions, setQuestions] = useState([])
  const [currentQuestion, setCurrentQuestion] = useState({
    question: '',
    type: 'SINGLE_CHOICE',
    points: 1,
    order: 1,
    isRequired: true,
    options: [
      { text: '', isCorrect: false, order: 1 },
      { text: '', isCorrect: false, order: 2 }
    ]
  })
  const [isQuestionModalOpen, setIsQuestionModalOpen] = useState(false)
  const [editingQuestionIndex, setEditingQuestionIndex] = useState(null)
  const [copyingTranslations, setCopyingTranslations] = useState(false)
  const [copyingQuestionId, setCopyingQuestionId] = useState(null)
  const [copyingQuiz, setCopyingQuiz] = useState(false)

  // Réinitialiser le formulaire quand l'entité change (sans initialLanguage)
  useEffect(() => {
    if (!initialLanguage) {
      setSelectedLanguage(null)
      setTranslationData({})
      setErrors({})
      setNewRequirement('')
      setNewObjective('')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId, type])

  // Charger la langue dans le formulaire quand initialLanguage change
  // Attendre que le chargement initial soit terminé et que availableLanguages soit chargé
  useEffect(() => {
    if (initialLanguage && SUPPORTED_LANGUAGES.some(l => l.code === initialLanguage) && entityId && type && !loading) {
      // Attendre un peu pour s'assurer que availableLanguages est bien chargé
      // Puis charger la langue (cela déclenchera le chargement avec loadingLanguage = true)
      const timer = setTimeout(() => {
        handleLanguageChange(initialLanguage)
      }, 200)
      
      return () => clearTimeout(timer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialLanguage, entityId, type, loading, availableLanguages])

  // Charger les langues disponibles
  useEffect(() => {
    if (!entityId || !type) return
    
    const loadLanguages = async () => {
      setLoading(true)
      try {
        let endpoint
        
        switch (type) {
          case 'course':
            endpoint = endpoints.getCourseLanguages(entityId)
            break
          case 'module':
            endpoint = endpoints.getModuleLanguages(entityId)
            break
          case 'lesson':
            endpoint = endpoints.getLessonLanguages(entityId)
            break
          case 'block':
            endpoint = endpoints.getBlockLanguages(entityId)
            break
          case 'quiz':
            endpoint = endpoints.getQuizLanguages(entityId)
            break
          default:
            setLoading(false)
            return
        }
        
        try {
          const response = await api.get(endpoint)
          // L'API retourne soit un tableau simple de codes de langue : ["en", "fr"]
          // soit un objet avec une propriété languages : { languages: ["en", "fr"], defaultLanguage: "fr" }
          let languages = []
          if (response?.data) {
            if (Array.isArray(response.data)) {
              languages = response.data
            } else if (response.data.languages && Array.isArray(response.data.languages)) {
              languages = response.data.languages
            } else {
              languages = response.data
            }
          } else if (Array.isArray(response)) {
            languages = response
          }
          
          // Filtrer pour ne garder que fr et en (les seules langues supportées)
          const supportedCodes = SUPPORTED_LANGUAGES.map(l => l.code)
          const filteredLanguages = languages.filter(lang => 
            typeof lang === 'string' && supportedCodes.includes(lang)
          )
          setAvailableLanguages(filteredLanguages)
          await loadAllTranslations(filteredLanguages)
        } catch (error) {
          console.log('Languages endpoint not available, will load from translations', error)
          setAvailableLanguages([])
          await loadAllTranslations(SUPPORTED_LANGUAGES.map(l => l.code))
        }
      } catch (err) {
        console.error('Error loading languages:', err)
        setAvailableLanguages([])
      } finally {
        setLoading(false)
      }
    }
    
    loadLanguages()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId, type])


  // Charger toutes les traductions existantes (seulement pour les langues disponibles)
  const loadAllTranslations = async (languages) => {
    const translationsMap = {}
    const foundLanguages = []
    
    // Charger seulement les traductions pour les langues qui sont dans la liste des langues disponibles
    // Utiliser le paramètre languages (qui est la liste des langues disponibles)
    for (const lang of languages) {
      try {
        let endpoint
        switch (type) {
          case 'course':
            endpoint = endpoints.getCourseTranslation(entityId, lang)
            break
          case 'module':
            endpoint = endpoints.getModuleTranslation(entityId, lang)
            break
          case 'lesson':
            endpoint = endpoints.getLessonTranslation(entityId, lang)
            break
          case 'block':
            endpoint = endpoints.getBlockTranslation(entityId, lang)
            break
          case 'quiz':
            endpoint = endpoints.getQuizTranslation(entityId, lang)
            break
          default:
            continue
        }
        
        const response = await api.get(endpoint)
        if (response?.data || response) {
          translationsMap[lang] = response.data || response
          foundLanguages.push(lang)
        }
      } catch (error) {
        // Traduction n'existe pas encore
        console.debug('Translation not found for language:', lang, error)
      }
    }
    
    setTranslations(translationsMap)
  }

  // Charger la traduction d'une langue spécifique (seulement si elle est dans la liste des langues disponibles)
  const loadTranslationForLanguage = async (langCode) => {
    // Vérifier d'abord si la traduction est déjà dans la liste
    if (translations[langCode]) {
      return translations[langCode]
    }
    
    // Vérifier si la langue est supportée
    if (!SUPPORTED_LANGUAGES.some(l => l.code === langCode)) {
      return null
    }
    
    // Si availableLanguages est vide, on peut quand même essayer de charger
    // (pour permettre l'ajout de nouvelles traductions ou si le chargement n'est pas encore terminé)
    // Sinon, charger seulement si la langue est dans la liste des langues disponibles
    if (availableLanguages.length > 0 && !availableLanguages.includes(langCode)) {
      // La langue n'est pas dans la liste des langues disponibles, ne pas charger
      return null
    }
    
    // La langue est dans la liste des langues disponibles (ou availableLanguages est vide), la charger
    try {
      let endpoint
      switch (type) {
        case 'course':
          endpoint = endpoints.getCourseTranslation(entityId, langCode)
          break
        case 'module':
          endpoint = endpoints.getModuleTranslation(entityId, langCode)
          break
        case 'lesson':
          endpoint = endpoints.getLessonTranslation(entityId, langCode)
          break
        case 'block':
          endpoint = endpoints.getBlockTranslation(entityId, langCode)
          break
        case 'quiz':
          endpoint = endpoints.getQuizTranslation(entityId, langCode)
          break
        default:
          return null
      }
      
      const response = await api.get(endpoint)
      let translation = response?.data || response || null
      
      console.log('loadTranslationForLanguage: Réponse brute de l\'API pour', type, langCode, ':', translation)
      
      // Pour les quiz, la réponse peut contenir toutes les propriétés du quiz
      // On doit extraire seulement les propriétés traduites (title, description, instructions)
      if (type === 'quiz' && translation) {
        // La réponse peut être un objet complet avec toutes les propriétés
        // On extrait seulement les propriétés traduites
        const quizTranslation = {
          title: translation.title || null,
          description: translation.description || null,
          instructions: translation.instructions || null
        }
        console.log('loadTranslationForLanguage: Traduction extraite pour quiz:', quizTranslation)
        // Ne garder que les propriétés qui ont une valeur (pas null)
        const filteredTranslation = {}
        Object.keys(quizTranslation).forEach(key => {
          if (quizTranslation[key] !== null && quizTranslation[key] !== undefined) {
            filteredTranslation[key] = quizTranslation[key]
          }
        })
        translation = Object.keys(filteredTranslation).length > 0 ? filteredTranslation : null
        console.log('loadTranslationForLanguage: Traduction filtrée pour quiz:', translation)
      }
      
      // Mettre à jour les traductions chargées
      if (translation) {
        setTranslations(prev => ({
          ...prev,
          [langCode]: translation
        }))
      }
      
      return translation
    } catch (error) {
      // Traduction n'existe pas encore
      console.debug('Translation not found for language:', langCode, error)
      return null
    }
  }

  // Charger les questions d'un quiz avec leurs traductions
  // Ne charge les traductions que si la langue est dans availableLanguages (comme pour les autres entités)
  const loadQuizQuestionsWithTranslations = async (langCode) => {
    if (!entityData?.questions || !Array.isArray(entityData.questions)) {
      setQuestions([])
      return
    }
    
    // Vérifier si la langue est dans la liste des langues disponibles
    // Si availableLanguages est vide, on peut quand même essayer de charger (pour permettre l'ajout de nouvelles traductions)
    // Sinon, charger seulement si la langue est dans la liste des langues disponibles
    const shouldLoadTranslations = availableLanguages.length === 0 || availableLanguages.includes(langCode)
    
    const questionsWithTranslations = []
    
    for (const question of entityData.questions) {
      if (!question.id) {
        // Question sans ID, utiliser les données de base
        questionsWithTranslations.push({
          ...question,
          order: questionsWithTranslations.length + 1
        })
        continue
      }
      
      // Charger la traduction de la question seulement si la langue est disponible
      let translatedQuestionText = question.question || ''
      if (shouldLoadTranslations) {
        try {
          const questionTranslation = await api.get(
            endpoints.getQuizQuestionTranslation(question.id, langCode)
          )
          if (questionTranslation?.data?.questionText || questionTranslation?.questionText) {
            translatedQuestionText = questionTranslation.data?.questionText || questionTranslation.questionText
          }
        } catch {
          // Traduction n'existe pas, utiliser le texte de base
          console.debug(`Traduction de la question ${question.id} non trouvée pour ${langCode}`)
        }
      }
      
      // Charger les traductions des options seulement si la langue est disponible
      const translatedOptions = []
      if (question.options && Array.isArray(question.options)) {
        for (const option of question.options) {
          if (!option.id) {
            // Option sans ID, utiliser les données de base
            translatedOptions.push({
              ...option,
              order: translatedOptions.length + 1
            })
            continue
          }
          
          let translatedOptionText = option.text || ''
          if (shouldLoadTranslations) {
            try {
              const optionTranslation = await api.get(
                endpoints.getQuizOptionTranslation(option.id, langCode)
              )
              if (optionTranslation?.data?.text || optionTranslation?.text) {
                translatedOptionText = optionTranslation.data?.text || optionTranslation.text
              }
            } catch {
              // Traduction n'existe pas, utiliser le texte de base
              console.debug(`Traduction de l'option ${option.id} non trouvée pour ${langCode}`)
            }
          }
          
          translatedOptions.push({
            ...option,
            text: translatedOptionText,
            order: translatedOptions.length + 1
          })
        }
      }
      
      questionsWithTranslations.push({
        ...question,
        question: translatedQuestionText,
        options: translatedOptions,
        order: questionsWithTranslations.length + 1
      })
    }
    
    setQuestions(questionsWithTranslations)
  }

  // Changer de langue
  const handleLanguageChange = async (langCode) => {
    setLoadingLanguage(true)
    setSelectedLanguage(langCode)
    setErrors({})
    setNewRequirement('')
    setNewObjective('')
    setQuestions([])
    setIsQuestionModalOpen(false)
    setEditingQuestionIndex(null)
    
    // Appeler le callback pour recharger l'entité traduite dans le contenu central
    if (onLanguageChange && entityId && type) {
      onLanguageChange(type, entityId, langCode)
    }
    
    try {
      // Si availableLanguages est vide, attendre un peu qu'il soit chargé
      // (peut arriver si on clique sur une langue avant que le chargement initial soit terminé)
      if (availableLanguages.length === 0 && loading) {
        // Attendre que le chargement soit terminé
        await new Promise(resolve => setTimeout(resolve, 300))
      }
      
      // Vérifier si la langue est dans la liste des langues disponibles
      // Si availableLanguages est toujours vide après l'attente, on peut quand même essayer de charger
      // (pour permettre l'ajout de nouvelles traductions)
      // Pour les quiz, on peut toujours charger même si la langue n'est pas dans availableLanguages
      // car on veut permettre l'ajout de nouvelles traductions
      if (availableLanguages.length > 0 && !availableLanguages.includes(langCode) && type !== 'quiz') {
        // La langue n'est pas dans la liste des langues disponibles, vider le formulaire
        // (sauf pour les quiz où on permet toujours le chargement)
        setTranslationData({})
        setLoadingLanguage(false)
        return
      }
      
      // Charger la traduction (loadTranslationForLanguage vérifiera aussi si la langue est disponible)
      const translation = await loadTranslationForLanguage(langCode)
      
      console.log('handleLanguageChange: Traduction chargée pour', langCode, ':', translation)
      
      const data = {}
      if (translation) {
        console.log('handleLanguageChange: Traduction trouvée pour', langCode, ':', translation)
        // Pour les quiz, extraire seulement les propriétés traduites
        if (type === 'quiz') {
          const allowedQuizProperties = ['title', 'description', 'instructions']
          allowedQuizProperties.forEach(prop => {
            // Prendre la valeur traduite si elle existe et n'est pas vide, sinon prendre la valeur de base depuis entityData
            if (translation[prop] !== undefined && translation[prop] !== null && translation[prop] !== '') {
              data[prop] = translation[prop]
            } else if (entityData?.[prop] !== undefined) {
              data[prop] = entityData[prop]
            } else {
              data[prop] = ''
            }
          })
          console.log('handleLanguageChange: Données traduites extraites pour quiz:', data)
          // Charger les questions depuis entityData et leurs traductions
          await loadQuizQuestionsWithTranslations(langCode)
        } else {
          // Pour les autres types, utiliser la traduction complète
          Object.assign(data, translation)
        }
        // S'assurer que le contenu est dans le bon format selon le type d'entité
        if (type === 'block') {
          if (entityData?.type === 'TEXT') {
            // Pour les blocs TEXT, le contenu doit être dans content.html
            if (data.content && typeof data.content === 'string') {
              data.content = { html: data.content }
            } else if (data.content && !data.content.html) {
              data.content = { html: data.content.html || '' }
            } else if (!data.content) {
              data.content = { html: '' }
            }
          } else if (entityData?.type === 'IMAGE') {
            // Pour les blocs IMAGE, le contenu doit être dans content.caption
            if (!data.content) {
              data.content = { caption: '' }
            } else if (typeof data.content === 'string') {
              data.content = { caption: data.content }
            } else if (!data.content.caption) {
              data.content = { caption: data.content.caption || '' }
            }
          } else if (entityData?.type === 'VIDEO') {
            // Pour les blocs VIDEO, le contenu doit être dans content.transcript
            if (!data.content) {
              data.content = { transcript: '' }
            } else if (typeof data.content === 'string') {
              data.content = { transcript: data.content }
            } else if (!data.content.transcript) {
              data.content = { transcript: data.content.transcript || '' }
            }
          }
        }
      } else {
        // Aucune traduction trouvée, pré-remplir avec les données de l'entité pour permettre la création
        if (type === 'course') {
          data.title = entityData?.title || ''
          data.shortDescription = entityData?.shortDescription || ''
          data.description = entityData?.description || ''
          data.requirements = entityData?.requirements ? [...(entityData.requirements || [])] : []
          data.objectives = entityData?.objectives ? [...(entityData.objectives || [])] : []
        } else if (type === 'module') {
          data.title = entityData?.title || ''
          data.description = entityData?.description || ''
        } else if (type === 'lesson') {
          data.title = entityData?.title || ''
          data.description = entityData?.description || ''
        } else if (type === 'block') {
          data.title = entityData?.title || ''
          data.description = entityData?.description || ''
          if (entityData?.type === 'IMAGE' && entityData?.content?.caption) {
            data.content = { caption: entityData.content.caption }
          } else if (entityData?.type === 'VIDEO' && entityData?.content?.transcript) {
            data.content = { transcript: entityData.content.transcript }
          } else if (entityData?.type === 'TEXT') {
            let htmlContent = ''
            if (entityData?.content?.html) {
              htmlContent = entityData.content.html
            } else if (typeof entityData?.content === 'string') {
              htmlContent = entityData.content
            }
            data.content = { html: htmlContent }
          } else {
            data.content = {}
          }
        } else if (type === 'quiz') {
          data.title = entityData?.title || ''
          data.description = entityData?.description || ''
          data.instructions = entityData?.instructions || ''
          // Charger les questions depuis entityData et leurs traductions
          await loadQuizQuestionsWithTranslations(langCode)
        }
      }
      
      console.log('TranslationManager: Définition de translationData avec:', data)
      setTranslationData(data)
      setLoadingLanguage(false)
    } catch (err) {
      console.error('Error loading language:', err)
      setLoadingLanguage(false)
    }
  }

  // Fermer le formulaire
  const handleCloseForm = () => {
    setSelectedLanguage(null)
    setTranslationData({})
    setErrors({})
    setNewRequirement('')
    setNewObjective('')
    setQuestions([])
    setIsQuestionModalOpen(false)
    setEditingQuestionIndex(null)
  }

  // Gérer les changements dans le formulaire
  const handleChange = (e) => {
    const { name, value, type: inputType, checked } = e.target
    // Empêcher la modification de maxAttempts (toujours 1 pour les quiz)
    if (name === 'maxAttempts' && type === 'quiz') {
      return
    }
    setTranslationData(prev => ({
      ...prev,
      [name]: inputType === 'checkbox' ? checked : (inputType === 'number' ? (value === '' ? null : parseFloat(value) || 0) : value)
    }))
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[name]
        return newErrors
      })
    }
  }

  // Gérer les changements pour les champs de contenu de bloc
  const handleContentChange = (field, value) => {
    // Pour les blocs TEXT, le contenu est directement dans content.html
    if (entityData?.type === 'TEXT' && field === 'html') {
      setTranslationData(prev => ({
        ...prev,
        content: {
          ...(prev.content || {}),
          html: value
        }
      }))
    } else {
      setTranslationData(prev => ({
        ...prev,
        content: {
          ...(prev.content || {}),
          [field]: value
        }
      }))
    }
  }

  // Gérer les changements pour le contenu HTML des blocs TEXT (via TipTapEditor)
  const handleTextContentChange = (html) => {
    setTranslationData(prev => ({
      ...prev,
      content: {
        ...(prev.content || {}),
        html: html
      }
    }))
  }

  // Fonctions pour gérer les questions de quiz
  const needsOptions = (type) => {
    return ['SINGLE_CHOICE', 'MULTIPLE_CHOICE', 'TRUE_FALSE', 'FILL_IN_THE_BLANK', 'NUMERIC', 'MATCHING'].includes(type)
  }

  const handleQuestionChange = (e) => {
    const { name, value, type } = e.target
    setCurrentQuestion(prev => ({
      ...prev,
      [name]: type === 'number' ? parseInt(value) || 1 : value
    }))
  }

  const handleOptionChange = (index, field, value) => {
    setCurrentQuestion(prev => ({
      ...prev,
      options: prev.options.map((opt, i) => 
        i === index ? { ...opt, [field]: value } : opt
      )
    }))
  }

  const handleAddOption = () => {
    setCurrentQuestion(prev => ({
      ...prev,
      options: [...prev.options, { text: '', isCorrect: false, order: prev.options.length + 1 }]
    }))
  }

  const handleRemoveOption = (index) => {
    setCurrentQuestion(prev => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index).map((opt, i) => ({ ...opt, order: i + 1 }))
    }))
  }

  const resetQuestionForm = () => {
    setCurrentQuestion({
      question: '',
      type: 'SINGLE_CHOICE',
      points: 1,
      order: questions.length + 1,
      isRequired: true,
      options: [
        { text: '', isCorrect: false, order: 1 },
        { text: '', isCorrect: false, order: 2 }
      ]
    })
    setEditingQuestionIndex(null)
  }

  const handleOpenQuestionModal = (index = null) => {
    if (index !== null) {
      // Éditer une question existante
      const question = questions[index]
      setCurrentQuestion({
        ...question,
        options: question.options && question.options.length > 0 
          ? question.options 
          : [
              { text: '', isCorrect: false, order: 1 },
              { text: '', isCorrect: false, order: 2 }
            ]
      })
      setEditingQuestionIndex(index)
    } else {
      // Nouvelle question
      resetQuestionForm()
    }
    setIsQuestionModalOpen(true)
  }

  const handleCloseQuestionModal = () => {
    setIsQuestionModalOpen(false)
    resetQuestionForm()
  }

  const handleAddQuestion = () => {
    // Validation
    if (!currentQuestion.question.trim()) {
      showErrorToast({ message: 'Veuillez saisir la question' })
      return
    }

    if (needsOptions(currentQuestion.type)) {
      const validOptions = currentQuestion.options.filter(opt => opt.text.trim())
      if (validOptions.length < 2) {
        showErrorToast({ message: 'Au moins 2 options sont requises' })
        return
      }
      
      // Vérifier qu'il y a au moins une bonne réponse
      const hasCorrectAnswer = validOptions.some(opt => opt.isCorrect)
      if (!hasCorrectAnswer) {
        showErrorToast({ message: 'Au moins une option doit être marquée comme correcte' })
        return
      }

      // Pour SINGLE_CHOICE et TRUE_FALSE, exactement 1 bonne réponse
      if ((currentQuestion.type === 'SINGLE_CHOICE' || currentQuestion.type === 'TRUE_FALSE') && 
          validOptions.filter(opt => opt.isCorrect).length !== 1) {
        showErrorToast({ message: 'Une seule option doit être marquée comme correcte pour ce type de question' })
        return
      }
    }

    // Si on édite une question existante
    if (editingQuestionIndex !== null) {
      const updatedQuestion = {
        ...currentQuestion,
        order: editingQuestionIndex + 1,
        options: currentQuestion.options.filter(opt => opt.text.trim())
      }
      setQuestions(prev => prev.map((q, i) => i === editingQuestionIndex ? updatedQuestion : q))
      setEditingQuestionIndex(null)
    } else {
      // Ajouter la question à la liste
      const newQuestion = {
        ...currentQuestion,
        order: questions.length + 1,
        options: currentQuestion.options.filter(opt => opt.text.trim())
      }
      setQuestions(prev => [...prev, newQuestion])
    }
    
    // Réinitialiser et fermer le modal
    resetQuestionForm()
    setIsQuestionModalOpen(false)
    showSuccessToast({ message: editingQuestionIndex !== null ? 'Question modifiée avec succès' : 'Question ajoutée avec succès' })
  }

  const handleRemoveQuestion = (index) => {
    setQuestions(prev => prev.filter((_, i) => i !== index).map((q, i) => ({ ...q, order: i + 1 })))
  }

  const handleQuestionMoveUp = (index) => {
    if (index === 0) return
    setQuestions(prev => {
      const newQuestions = [...prev]
      const temp = newQuestions[index]
      newQuestions[index] = { ...newQuestions[index - 1], order: index + 1 }
      newQuestions[index - 1] = { ...temp, order: index }
      return newQuestions
    })
  }

  const handleQuestionMoveDown = (index) => {
    if (index >= questions.length - 1) return
    setQuestions(prev => {
      const newQuestions = [...prev]
      const temp = newQuestions[index]
      newQuestions[index] = { ...newQuestions[index + 1], order: index + 1 }
      newQuestions[index + 1] = { ...temp, order: index + 2 }
      return newQuestions
    })
  }

  const handleOptionMoveUp = (optionIndex) => {
    if (optionIndex === 0) return
    setCurrentQuestion(prev => {
      const newOptions = [...prev.options]
      const temp = newOptions[optionIndex]
      newOptions[optionIndex] = { ...newOptions[optionIndex - 1], order: optionIndex + 1 }
      newOptions[optionIndex - 1] = { ...temp, order: optionIndex }
      return { ...prev, options: newOptions }
    })
  }

  const handleOptionMoveDown = (optionIndex) => {
    if (optionIndex >= currentQuestion.options.length - 1) return
    setCurrentQuestion(prev => {
      const newOptions = [...prev.options]
      const temp = newOptions[optionIndex]
      newOptions[optionIndex] = { ...newOptions[optionIndex + 1], order: optionIndex + 1 }
      newOptions[optionIndex + 1] = { ...temp, order: optionIndex + 2 }
      return { ...prev, options: newOptions }
    })
  }

  // Copier les traductions d'une question vers l'autre langue (si on est en fr, copier vers en, et vice versa)
  const handleCopyQuestionTranslations = async (questionId) => {
    if (!questionId) {
      showErrorToast({ message: 'ID de question manquant' })
      return
    }

    // Récupérer la langue actuellement sélectionnée
    const currentLang = selectedLanguage || initialLanguage
    if (!currentLang || !SUPPORTED_LANGUAGES.some(l => l.code === currentLang)) {
      showErrorToast({ message: 'Veuillez sélectionner une langue (fr ou en) avant de copier' })
      return
    }

    // Déterminer la langue cible (l'autre langue que celle actuellement sélectionnée)
    const targetLang = currentLang === 'fr' ? 'en' : 'fr'

    setCopyingQuestionId(questionId)
    setCopyingTranslations(true)

    try {
      // Passer la langue cible dans le paramètre language de l'endpoint
      // apiClient extrait déjà data.data si la réponse est { success: true, data: {...} }
      // Donc response contient directement { question, options }
      const responseData = await api.post(endpoints.copyQuizQuestionTranslations(questionId, targetLang))

      // Compter les traductions retournées
      const questionCount = responseData?.question ? 1 : 0
      const optionsCount = (responseData?.options && Array.isArray(responseData.options)) ? responseData.options.length : 0
      
      // Vérifier si on a des données valides
      if (questionCount > 0 || optionsCount > 0) {
        // Afficher un message de succès avec le décompte
        const successMessage = `${questionCount} traduction(s) de question créée(s), ${optionsCount} traduction(s) d'option créée(s)`
        
        showSuccessToast({ message: successMessage })
        
        // Recharger complètement le formulaire de gestion des traductions
        if (currentLang && type === 'quiz') {
          // Recharger les questions avec leurs traductions
          await loadQuizQuestionsWithTranslations(currentLang)
          
          // Recharger la traduction du quiz pour mettre à jour le formulaire
          const translation = await loadTranslationForLanguage(currentLang)
          if (translation) {
            const data = {}
            Object.assign(data, translation)
            setTranslationData(prev => ({
              ...prev,
              ...data
            }))
          }
          
          // Mettre à jour les langues disponibles
          try {
            const languagesResponse = await api.get(endpoints.getQuizLanguages(entityId))
            let allLanguages = []
            if (languagesResponse?.data) {
              if (Array.isArray(languagesResponse.data)) {
                allLanguages = languagesResponse.data
              } else if (languagesResponse.data.languages && Array.isArray(languagesResponse.data.languages)) {
                allLanguages = languagesResponse.data.languages
              }
            }
            const supportedCodes = SUPPORTED_LANGUAGES.map(l => l.code)
            const filteredLanguages = allLanguages.filter(lang => 
              typeof lang === 'string' && supportedCodes.includes(lang)
            )
            setAvailableLanguages(filteredLanguages)
            
            // Si la langue cible n'était pas dans la liste, l'ajouter
            if (!filteredLanguages.includes(targetLang) && SUPPORTED_LANGUAGES.some(l => l.code === targetLang)) {
              setAvailableLanguages(prev => [...prev, targetLang])
            }
          } catch (err) {
            console.debug('Error loading quiz languages after copy:', err)
          }
        }
      } else {
        console.error('Failed to parse response - no valid data found:', responseData)
        showErrorToast({ message: 'Erreur lors de la copie des traductions - aucune donnée valide trouvée' })
      }
    } catch (err) {
      console.error('Error copying question translations:', err)
      showErrorToast({ message: err?.response?.data?.message || 'Erreur lors de la copie des traductions' })
    } finally {
      setCopyingQuestionId(null)
      setCopyingTranslations(false)
    }
  }

  // Copier les traductions de toutes les questions vers l'autre langue (si on est en fr, copier vers en, et vice versa)
  const handleCopyAllQuestionsTranslations = async () => {
    if (!questions || questions.length === 0) {
      showErrorToast({ message: 'Aucune question à copier' })
      return
    }

    // Récupérer la langue actuellement sélectionnée
    const currentLang = selectedLanguage || initialLanguage
    if (!currentLang || !SUPPORTED_LANGUAGES.some(l => l.code === currentLang)) {
      showErrorToast({ message: 'Veuillez sélectionner une langue (fr ou en) avant de copier' })
      return
    }

    // Déterminer la langue cible (l'autre langue que celle actuellement sélectionnée)
    const targetLang = currentLang === 'fr' ? 'en' : 'fr'

    setCopyingTranslations(true)

    try {
      let totalQuestionCreated = 0
      let totalOptionsCreated = 0
      const errors = []

      for (const question of questions) {
        if (!question.id) {
          errors.push(`Question ${question.order || 'sans ordre'} : ID manquant`)
          continue
        }

        try {
          // Passer la langue cible dans le paramètre language de l'endpoint
          // apiClient extrait déjà data.data si la réponse est { success: true, data: {...} }
          // Donc responseData contient directement { question, options }
          const responseData = await api.post(endpoints.copyQuizQuestionTranslations(question.id, targetLang))
          
          // Compter les traductions retournées
          if (responseData?.question) {
            totalQuestionCreated += 1
          }
          if (responseData?.options && Array.isArray(responseData.options)) {
            totalOptionsCreated += responseData.options.length
          }
        } catch (err) {
          errors.push(`Question ${question.order || 'sans ordre'} : ${err?.response?.data?.message || 'Erreur'}`)
        }
      }

      if (errors.length > 0) {
        showErrorToast({ message: `${errors.length} erreur(s) lors de la copie. ${totalQuestionCreated} traduction(s) créée(s) avec succès.` })
      } else {
        showSuccessToast({ 
          message: `${totalQuestionCreated} traduction(s) de question créée(s), ${totalOptionsCreated} traduction(s) d'option créée(s)` 
        })
      }

      // Recharger complètement le formulaire de gestion des traductions
      if (currentLang && type === 'quiz') {
        // Recharger les questions avec leurs traductions
        await loadQuizQuestionsWithTranslations(currentLang)
        
        // Recharger la traduction du quiz pour mettre à jour le formulaire
        const translation = await loadTranslationForLanguage(currentLang)
        if (translation) {
          const data = {}
          Object.assign(data, translation)
          setTranslationData(prev => ({
            ...prev,
            ...data
          }))
        }
        
        // Mettre à jour les langues disponibles
        try {
          const languagesResponse = await api.get(endpoints.getQuizLanguages(entityId))
          let allLanguages = []
          if (languagesResponse?.data) {
            if (Array.isArray(languagesResponse.data)) {
              allLanguages = languagesResponse.data
            } else if (languagesResponse.data.languages && Array.isArray(languagesResponse.data.languages)) {
              allLanguages = languagesResponse.data.languages
            }
          }
          const supportedCodes = SUPPORTED_LANGUAGES.map(l => l.code)
          const filteredLanguages = allLanguages.filter(lang => 
            typeof lang === 'string' && supportedCodes.includes(lang)
          )
          setAvailableLanguages(filteredLanguages)
          
          // Si la langue cible n'était pas dans la liste, l'ajouter
          if (!filteredLanguages.includes(targetLang) && SUPPORTED_LANGUAGES.some(l => l.code === targetLang)) {
            setAvailableLanguages(prev => [...prev, targetLang])
          }
        } catch (err) {
          console.debug('Error loading quiz languages after copy:', err)
        }
      }
    } catch (err) {
      console.error('Error copying all questions translations:', err)
      showErrorToast({ message: 'Erreur lors de la copie des traductions' })
    } finally {
      setCopyingTranslations(false)
    }
  }

  // Copier le quiz complet vers une traduction
  const handleCopyQuizTranslations = async () => {
    if (!entityId) {
      showErrorToast({ message: 'ID du quiz manquant' })
      return
    }

    // Récupérer la langue actuellement sélectionnée
    const currentLang = selectedLanguage || initialLanguage
    if (!currentLang || !SUPPORTED_LANGUAGES.some(l => l.code === currentLang)) {
      showErrorToast({ message: 'Veuillez sélectionner une langue (fr ou en) avant de copier' })
      return
    }

    // Déterminer la langue cible (l'autre langue que celle actuellement sélectionnée)
    const targetLang = currentLang === 'fr' ? 'en' : 'fr'

    setCopyingQuiz(true)

    try {
      // Appeler l'API pour copier le quiz complet
      const response = await api.post(endpoints.copyQuizTranslations(entityId, targetLang))
      const data = response?.data || response

      if (data?.success) {
        const messages = data.messages || []
        const responseData = data.data || {}
        
        // Afficher un message de succès
        const successMessage = messages.join(', ') || 'Traductions du quiz créées/mises à jour avec succès'
        showSuccessToast({ message: successMessage })

        // Mettre à jour les données du quiz dans translationData si la traduction du quiz est retournée
        if (responseData.quiz) {
          const quizTranslation = responseData.quiz
          setTranslationData(prev => ({
            ...prev,
            title: quizTranslation.title || prev.title,
            description: quizTranslation.description || prev.description,
            instructions: quizTranslation.instructions || prev.instructions
          }))
        }

        // Recharger la traduction du quiz pour la langue cible
        const targetTranslation = await loadTranslationForLanguage(targetLang)
        if (targetTranslation) {
          // Mettre à jour translationData avec la traduction du quiz
          setTranslationData(prev => ({
            ...prev,
            title: targetTranslation.title || prev.title,
            description: targetTranslation.description || prev.description,
            instructions: targetTranslation.instructions || prev.instructions,
            timeLimit: targetTranslation.timeLimit !== undefined ? targetTranslation.timeLimit : prev.timeLimit,
            passingScore: targetTranslation.passingScore !== undefined ? targetTranslation.passingScore : prev.passingScore,
            maxAttempts: 1, // Toujours 1 tentative pour les quiz
            isRandomized: targetTranslation.isRandomized !== undefined ? targetTranslation.isRandomized : prev.isRandomized,
            isPublished: targetTranslation.isPublished !== undefined ? targetTranslation.isPublished : prev.isPublished
          }))
        }

        // Recharger les questions avec leurs traductions pour la langue cible
        if (targetLang && type === 'quiz') {
          await loadQuizQuestionsWithTranslations(targetLang)
        }

        // Mettre à jour les langues disponibles
        try {
          const languagesResponse = await api.get(endpoints.getQuizLanguages(entityId))
          let allLanguages = []
          if (languagesResponse?.data) {
            if (Array.isArray(languagesResponse.data)) {
              allLanguages = languagesResponse.data
            } else if (languagesResponse.data.languages && Array.isArray(languagesResponse.data.languages)) {
              allLanguages = languagesResponse.data.languages
            }
          }
          const supportedCodes = SUPPORTED_LANGUAGES.map(l => l.code)
          const filteredLanguages = allLanguages.filter(lang => 
            typeof lang === 'string' && supportedCodes.includes(lang)
          )
          setAvailableLanguages(filteredLanguages)
          
          // Si la langue cible n'était pas dans la liste, l'ajouter
          if (!filteredLanguages.includes(targetLang) && SUPPORTED_LANGUAGES.some(l => l.code === targetLang)) {
            setAvailableLanguages(prev => [...prev, targetLang])
          }
        } catch (err) {
          console.debug('Error loading quiz languages after copy:', err)
        }

        // Basculer vers la langue cible pour afficher les nouvelles traductions
        setSelectedLanguage(targetLang)
        
        // Recharger les données pour la langue cible (déjà fait plus haut, mais on s'assure que tout est à jour)
        await handleLanguageChange(targetLang)

        // Notifier le parent pour mettre à jour l'inspecteur
        if (onTranslationSaved) {
          onTranslationSaved(targetLang, responseData.quiz)
        }

        // Recharger la page pour mettre à jour toutes les données
        window.location.reload()
      } else {
        showErrorToast({ message: 'Erreur lors de la copie des traductions du quiz' })
      }
    } catch (err) {
      console.error('Error copying quiz translations:', err)
      showErrorToast({ message: err?.response?.data?.message || 'Erreur lors de la copie des traductions du quiz' })
    } finally {
      setCopyingQuiz(false)
    }
  }

  // Ajouter un élément à un tableau (requirements, objectives)
  const handleAddItem = (field, value, setter) => {
    if (value.trim()) {
      setTranslationData(prev => ({
        ...prev,
        [field]: [...(prev[field] || []), value.trim()]
      }))
      setter('')
    }
  }

  // Supprimer un élément d'un tableau
  const handleRemoveItem = (field, index) => {
    setTranslationData(prev => ({
      ...prev,
      [field]: (prev[field] || []).filter((_, i) => i !== index)
    }))
  }

  // Valider le formulaire
  const validate = () => {
    const newErrors = {}
    if (!translationData.title?.trim()) {
      newErrors.title = 'Le titre est obligatoire'
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Sauvegarder la traduction
  const handleSave = async () => {
    if (!validate()) {
      showErrorToast({ message: 'Veuillez corriger les erreurs dans le formulaire' })
      return
    }

    setSaving(true)
    try {
      // Déterminer la langue active du formulaire
      // La langue active est celle qui est actuellement sélectionnée et affichée dans le formulaire
      // selectedLanguage est défini quand l'utilisateur clique sur une langue dans le sélecteur (priorité)
      // initialLanguage est la langue passée en prop lors de l'ouverture du formulaire (fallback)
      // IMPORTANT: On utilise selectedLanguage en priorité car c'est la langue actuellement active dans le formulaire
      let currentLang = selectedLanguage
      
      // Si selectedLanguage n'est pas défini, utiliser initialLanguage
      if (!currentLang) {
        currentLang = initialLanguage
      }
      
      // Si toujours pas de langue, chercher quelle langue est actuellement sélectionnée visuellement
      if (!currentLang) {
        // Chercher la langue qui est actuellement sélectionnée dans les boutons
        const activeLang = SUPPORTED_LANGUAGES.find(lang => {
          // Vérifier si cette langue est actuellement sélectionnée
          return (selectedLanguage === lang.code) || (!selectedLanguage && initialLanguage === lang.code)
        })
        if (activeLang) {
          currentLang = activeLang.code
        }
      }
      
      if (!currentLang) {
        showErrorToast({ message: 'Veuillez sélectionner une langue avant de sauvegarder' })
        setSaving(false)
        return
      }
      
      // Vérifier que la langue est supportée
      if (!SUPPORTED_LANGUAGES.some(l => l.code === currentLang)) {
        showErrorToast({ message: `Langue non supportée: ${currentLang}` })
        setSaving(false)
        return
      }
      
      // Log pour déboguer
      console.log('🔍 Sauvegarde de la traduction:', {
        currentLang,
        selectedLanguage,
        initialLanguage,
        'selectedLanguage défini': !!selectedLanguage,
        'initialLanguage défini': !!initialLanguage,
        'Langue utilisée': currentLang
      })
      
      // Liste des propriétés système à retirer
      const systemPropertiesToRemove = ['id', 'courseId', 'moduleId', 'blockId', 'quizId', 'createdAt', 'updatedAt', 'deletedAt']
      
      // Nettoyer les données en retirant les propriétés système qui ne doivent pas être envoyées
      let cleanedData = { ...translationData }
      systemPropertiesToRemove.forEach(prop => {
        delete cleanedData[prop]
      })
      
      // Pour les quiz, ne garder que les propriétés pertinentes pour la traduction
      if (type === 'quiz') {
        const allowedQuizProperties = ['title', 'description', 'instructions']
        const filteredData = {}
        allowedQuizProperties.forEach(prop => {
          if (cleanedData[prop] !== undefined) {
            filteredData[prop] = cleanedData[prop]
          }
        })
        cleanedData = filteredData
      }

      let endpoint
      let method = 'POST'
      
      const translationExists = translations[currentLang]
      
      if (translationExists) {
        method = 'PATCH'
      }
      
      // Déterminer l'endpoint selon le type et si la traduction existe
      if (translationExists) {
        switch (type) {
          case 'course':
            endpoint = endpoints.updateCourseTranslation(entityId, currentLang)
            break
          case 'module':
            endpoint = endpoints.updateModuleTranslation(entityId, currentLang)
            break
          case 'lesson':
            endpoint = endpoints.updateLessonTranslation(entityId, currentLang)
            break
          case 'block':
            endpoint = endpoints.updateBlockTranslation(entityId, currentLang)
            break
          case 'quiz':
            endpoint = endpoints.updateQuizTranslation(entityId, currentLang)
            break
        }
      } else {
        switch (type) {
          case 'course':
            endpoint = endpoints.createCourseTranslation(entityId)
            break
          case 'module':
            endpoint = endpoints.createModuleTranslation(entityId)
            break
          case 'lesson':
            endpoint = endpoints.createLessonTranslation(entityId)
            break
          case 'block':
            endpoint = endpoints.createBlockTranslation(entityId)
            break
          case 'quiz':
            endpoint = endpoints.createQuizTranslation(entityId)
            break
        }
      }
      
      // Ne pas inclure 'language' lors d'un PATCH (mise à jour), car la langue est déjà dans l'URL
      // Inclure 'language' seulement lors d'un POST (création)
      const dataToSave = method === 'POST' 
        ? { language: currentLang, ...cleanedData }
        : { ...cleanedData }

      // Sauvegarder la traduction du quiz (title, description, instructions uniquement)
      const response = await api[method.toLowerCase()](endpoint, dataToSave)
      
      // Si c'est un quiz, sauvegarder les traductions des questions et options
      if (type === 'quiz' && questions.length > 0) {
        for (const question of questions) {
          if (!question.id) {
            console.warn('Question sans ID, impossible de sauvegarder la traduction:', question)
            continue
          }
          
          // Sauvegarder la traduction de la question
          if (question.question && question.question.trim()) {
            try {
              // Utiliser POST pour créer/mettre à jour la traduction
              await api.post(endpoints.createQuizQuestionTranslation(question.id), {
                language: currentLang,
                questionText: question.question
              })
            } catch (err) {
              console.error(`Erreur lors de la sauvegarde de la traduction de la question ${question.id}:`, err)
              // Continuer avec les autres questions même en cas d'erreur
            }
          }
          
          // Sauvegarder les traductions des options
          if (question.options && Array.isArray(question.options)) {
            for (const option of question.options) {
              if (!option.id) {
                console.warn('Option sans ID, impossible de sauvegarder la traduction:', option)
                continue
              }
              
              if (option.text && option.text.trim()) {
                try {
                  // Utiliser POST pour créer/mettre à jour la traduction
                  await api.post(endpoints.createQuizOptionTranslation(option.id), {
                    language: currentLang,
                    text: option.text
                  })
                } catch (err) {
                  console.error(`Erreur lors de la sauvegarde de la traduction de l'option ${option.id}:`, err)
                  // Continuer avec les autres options même en cas d'erreur
                }
              }
            }
          }
        }
      }
      
      // Récupérer les données de la réponse de l'API
      const savedTranslation = response?.data || response || dataToSave
      
      // Mettre à jour les traductions chargées avec la réponse de l'API
      setTranslations(prev => ({
        ...prev,
        [currentLang]: savedTranslation
      }))
      
      if (!availableLanguages.includes(currentLang)) {
        setAvailableLanguages(prev => [...prev, currentLang])
      }
      
      // Mettre à jour le formulaire avec les données de la réponse
      // Nettoyer les propriétés système pour l'affichage dans le formulaire
      let cleanedResponse = { ...savedTranslation }
      systemPropertiesToRemove.forEach(prop => {
        delete cleanedResponse[prop]
      })
      
      // Pour les quiz, ne garder que les propriétés pertinentes
      if (type === 'quiz') {
        const allowedQuizProperties = ['title', 'description', 'instructions']
        const filteredResponse = {}
        allowedQuizProperties.forEach(prop => {
          if (cleanedResponse[prop] !== undefined) {
            filteredResponse[prop] = cleanedResponse[prop]
          }
        })
        cleanedResponse = filteredResponse
      }
      
      // S'assurer que le contenu est dans le bon format selon le type d'entité
      if (type === 'block') {
        if (entityData?.type === 'TEXT') {
          // Pour les blocs TEXT, le contenu doit être dans content.html
          if (cleanedResponse.content && typeof cleanedResponse.content === 'string') {
            cleanedResponse.content = { html: cleanedResponse.content }
          } else if (cleanedResponse.content && !cleanedResponse.content.html) {
            cleanedResponse.content = { html: cleanedResponse.content.html || '' }
          } else if (!cleanedResponse.content) {
            cleanedResponse.content = { html: '' }
          }
        } else if (entityData?.type === 'IMAGE') {
          // Pour les blocs IMAGE, le contenu doit être dans content.caption
          if (!cleanedResponse.content) {
            cleanedResponse.content = { caption: '' }
          } else if (typeof cleanedResponse.content === 'string') {
            cleanedResponse.content = { caption: cleanedResponse.content }
          } else if (!cleanedResponse.content.caption) {
            cleanedResponse.content = { caption: cleanedResponse.content.caption || '' }
          }
        } else if (entityData?.type === 'VIDEO') {
          // Pour les blocs VIDEO, le contenu doit être dans content.transcript
          if (!cleanedResponse.content) {
            cleanedResponse.content = { transcript: '' }
          } else if (typeof cleanedResponse.content === 'string') {
            cleanedResponse.content = { transcript: cleanedResponse.content }
          } else if (!cleanedResponse.content.transcript) {
            cleanedResponse.content = { transcript: cleanedResponse.content.transcript || '' }
          }
        }
      }
      
      setTranslationData(cleanedResponse)
      
      showSuccessToast({ message: `Traduction ${currentLang.toUpperCase()} sauvegardée avec succès !` })
      
      // Si c'est un quiz, mettre à jour aussi le quiz de base avec toutes les questions et options
      // Utiliser les questions originales depuis entityData (pas les traductions) pour mettre à jour le quiz de base
      if (type === 'quiz' && entityId && entityData && entityData.questions && Array.isArray(entityData.questions)) {
        try {
          // Fonction helper pour vérifier si un type de question nécessite des options
          const questionNeedsOptions = (type) => {
            return ['SINGLE_CHOICE', 'MULTIPLE_CHOICE', 'TRUE_FALSE', 'FILL_IN_THE_BLANK', 'NUMERIC', 'MATCHING'].includes(type)
          }
          
          // Utiliser les questions originales depuis entityData (qui contiennent le texte original, pas les traductions)
          // Respecter l'ordre et la présence des questions dans le state questions si elles ont été modifiées
          // Sinon, utiliser directement entityData.questions
          const baseQuestions = entityData.questions
          
          // Préparer les questions avec leurs options au format attendu par l'API
          const questionsData = baseQuestions.map((q, index) => {
            const questionData = {
              ...(q.id ? { id: q.id } : {}), // Inclure l'ID seulement si la question existe
              question: q.question || '', // Utiliser le texte original de la question
              type: q.type || 'SINGLE_CHOICE',
              points: q.points || 1,
              order: q.order || (index + 1),
              isRequired: q.isRequired !== undefined ? q.isRequired : true,
              ...(questionNeedsOptions(q.type) && q.options && Array.isArray(q.options) && q.options.length > 0 ? {
                options: q.options
                  .filter(opt => opt.text && opt.text.trim()) // Filtrer les options vides
                  .map((opt, optIndex) => ({
                    ...(opt.id ? { id: opt.id } : {}), // Inclure l'ID seulement si l'option existe
                    text: opt.text.trim(), // Utiliser le texte original de l'option
                    isCorrect: opt.isCorrect || false,
                    order: opt.order || (optIndex + 1)
                  }))
              } : {})
            }
            return questionData
          })

          // Préparer les données complètes pour l'API
          // Utiliser les données du quiz de base (entityData) pour les propriétés du quiz
          const completeQuizData = {
            title: entityData.title || '',
            description: entityData.description || '',
            instructions: entityData.instructions || '',
            timeLimit: entityData.timeLimit || null,
            passingScore: entityData.passingScore || 70,
            maxAttempts: 1, // Toujours 1 tentative pour les quiz
            isRandomized: entityData.isRandomized !== undefined ? entityData.isRandomized : false,
            isPublished: entityData.isPublished !== undefined ? entityData.isPublished : true,
            questions: questionsData // Inclure toutes les questions (celles avec ID seront mises à jour, celles sans ID seront créées, celles absentes seront supprimées)
          }

          // Utiliser l'API PUT /quiz/:id/complete pour mettre à jour le quiz complet
          await api.put(endpoints.quizComplete(entityId), completeQuizData)
          console.log('Quiz de base mis à jour avec succès avec', questionsData.length, 'question(s)')
        } catch (err) {
          console.error('Erreur lors de la mise à jour du quiz de base:', err)
          // Ne pas bloquer la sauvegarde des traductions si la mise à jour du quiz échoue
          showErrorToast({ message: 'Traductions sauvegardées, mais erreur lors de la mise à jour du quiz de base' })
        }
      }
      
      if (onTranslationSaved) {
        onTranslationSaved(currentLang, savedTranslation)
      }
      
      // Ne pas fermer le formulaire, le garder rempli avec les données de la réponse
    } catch (err) {
      console.error('Error saving translation:', err)
      showErrorToast({ message: err?.response?.data?.message || 'Erreur lors de la sauvegarde de la traduction' })
    } finally {
      setSaving(false)
    }
  }


  // Si l'entité n'a pas encore d'ID
  if (!entityId) {
    return (
      <div className="flex flex-col items-center justify-center p-8 h-full">
        <AlertCircle className="w-12 h-12 text-gray-400 mb-4" />
        <p className="text-gray-600 text-center">
          Veuillez d'abord sauvegarder {type === 'course' ? 'le cours' : type === 'module' ? 'le module' : type === 'lesson' ? 'la leçon' : type === 'block' ? 'le bloc' : 'le quiz'} avant de pouvoir ajouter des traductions.
        </p>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-6 h-6 animate-spin text-[#141835]" />
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full bg-gray-50">
      {/* Header */}
      {!hideHeader && type !== 'quiz' && (
        <div className="bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#141835] rounded-lg">
                <Languages className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Gestion des traductions</h2>
                <p className="text-sm text-gray-600 mt-1">
                  Gérez les traductions pour {type === 'course' ? 'ce cours' : type === 'module' ? 'ce module' : type === 'lesson' ? 'cette leçon' : type === 'block' ? 'ce bloc' : 'ce quiz'}
                </p>
              </div>
            </div>
            {onClose && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="h-8"
              >
                <X className="w-4 h-4 mr-2" />
                Fermer
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {loadingLanguage ? (
          // Afficher un loader pendant le chargement de la langue
          <div className="flex items-center justify-center p-8">
            <Loader2 className="w-6 h-6 animate-spin text-[#141835]" />
            <span className="ml-3 text-gray-600">Chargement de la traduction...</span>
          </div>
        ) : (selectedLanguage || initialLanguage) ? (
          // Formulaire de traduction - Structure identique aux formulaires de création
          <div className="max-w-4xl mx-auto">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Globe className="w-5 h-5 text-blue-600" />
                  <h3 className="text-lg font-semibold text-blue-900">
                    Traduction en {SUPPORTED_LANGUAGES.find(l => l.code === (selectedLanguage || initialLanguage))?.name || (selectedLanguage || initialLanguage)?.toUpperCase()}
                  </h3>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleCloseForm}
                  className="h-8"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              
              {/* Sélecteur de langue */}
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-blue-900">Changer de langue :</label>
                <div className="flex gap-2">
                  {SUPPORTED_LANGUAGES.map(lang => {
                    const isSelected = (selectedLanguage || initialLanguage) === lang.code
                    const hasTranslation = translations[lang.code] !== undefined
                    
                    return (
                      <button
                        key={lang.code}
                        onClick={() => handleLanguageChange(lang.code)}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                          isSelected
                            ? 'bg-blue-600 text-white'
                            : hasTranslation
                            ? 'bg-white text-blue-700 hover:bg-blue-100 border border-blue-300'
                            : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-300'
                        }`}
                      >
                        <span>{lang.flag}</span>
                        <span>{lang.name}</span>
                        {hasTranslation && !isSelected && (
                          <CheckCircle className="w-3 h-3" />
                        )}
                      </button>
                    )
                  })}
                </div>
              </div>
            </div>

            <form onSubmit={(e) => { e.preventDefault(); handleSave(); }} className="max-w-4xl mx-auto">
              <div className="space-y-8">
                {/* COURSE FORM - Structure identique à CourseEditForm */}
                {type === 'course' && (selectedLanguage || initialLanguage) && (
                  <>
                    {/* Basic Information */}
                    <div className="bg-white rounded-lg shadow-sm p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <FileText className="w-5 h-5" />
                        Informations de base
                      </h3>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Titre <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            name="title"
                            value={translationData.title || ''}
                            onChange={handleChange}
                            className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835] ${
                              errors.title ? 'border-red-500' : 'border-gray-300'
                            }`}
                            placeholder="Titre du cours"
                            required
                          />
                          {errors.title && (
                            <p className="mt-1 text-sm text-red-600">{errors.title}</p>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Description courte
                          </label>
                          <input
                            type="text"
                            name="shortDescription"
                            value={translationData.shortDescription || ''}
                            onChange={handleChange}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
                            placeholder="Description courte"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Description complète
                          </label>
                          <textarea
                            name="description"
                            value={translationData.description || ''}
                            onChange={handleChange}
                            rows={4}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
                            placeholder="Description détaillée du cours"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Requirements */}
                    <div className="bg-white rounded-lg shadow-sm p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Prérequis</h3>
                      <div className="space-y-2">
                        {(translationData.requirements || []).map((req, index) => (
                          <div key={index} className="flex items-center gap-2">
                            <span className="flex-1 text-sm text-gray-700 bg-gray-50 px-3 py-2 rounded">
                              {req}
                            </span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveItem('requirements', index)}
                              className="h-8 w-8 p-0 text-red-600"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={newRequirement}
                            onChange={(e) => setNewRequirement(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleAddItem('requirements', newRequirement, setNewRequirement)}
                            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
                            placeholder="Ajouter un prérequis"
                          />
                          <Button
                            type="button"
                            size="sm"
                            onClick={() => handleAddItem('requirements', newRequirement, setNewRequirement)}
                            className="bg-[#141835] hover:bg-[#1a1f4a] text-white"
                          >
                            <Plus className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>

                    {/* Objectives */}
                    <div className="bg-white rounded-lg shadow-sm p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Objectifs</h3>
                      <div className="space-y-2">
                        {(translationData.objectives || []).map((obj, index) => (
                          <div key={index} className="flex items-center gap-2">
                            <span className="flex-1 text-sm text-gray-700 bg-gray-50 px-3 py-2 rounded">
                              {obj}
                            </span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveItem('objectives', index)}
                              className="h-8 w-8 p-0 text-red-600"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={newObjective}
                            onChange={(e) => setNewObjective(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleAddItem('objectives', newObjective, setNewObjective)}
                            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
                            placeholder="Ajouter un objectif"
                          />
                          <Button
                            type="button"
                            size="sm"
                            onClick={() => handleAddItem('objectives', newObjective, setNewObjective)}
                            className="bg-[#141835] hover:bg-[#1a1f4a] text-white"
                          >
                            <Plus className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </>
                )}

              {/* MODULE FORM - Structure identique à ModuleForm */}
              {type === 'module' && (selectedLanguage || initialLanguage) && (
                <div className="max-w-2xl mx-auto">
                  <div className="bg-white rounded-lg shadow-sm p-6 space-y-6">
                    {/* Title */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Titre <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="title"
                        value={translationData.title || ''}
                        onChange={handleChange}
                        className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835] ${
                          errors.title ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="Ex: Introduction à React"
                        required
                      />
                      {errors.title && (
                        <p className="mt-1 text-sm text-red-600">{errors.title}</p>
                      )}
                    </div>

                    {/* Description */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Description
                      </label>
                      <textarea
                        name="description"
                        value={translationData.description || ''}
                        onChange={handleChange}
                        rows={4}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
                        placeholder="Description du module..."
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* LESSON FORM - Structure identique à LessonForm */}
              {type === 'lesson' && (selectedLanguage || initialLanguage) && (
                <div className="max-w-2xl mx-auto">
                  <div className="bg-white rounded-lg shadow-sm p-6 space-y-6">
                    {/* Title */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Titre <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="title"
                        value={translationData.title || ''}
                        onChange={handleChange}
                        className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835] ${
                          errors.title ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="Ex: Introduction aux Hooks"
                        required
                      />
                      {errors.title && (
                        <p className="mt-1 text-sm text-red-600">{errors.title}</p>
                      )}
                    </div>

                    {/* Description */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Description
                      </label>
                      <textarea
                        name="description"
                        value={translationData.description || ''}
                        onChange={handleChange}
                        rows={4}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
                        placeholder="Description du chapitre..."
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* BLOCK FORM - Structure identique à BlockForm */}
              {type === 'block' && (selectedLanguage || initialLanguage) && (
                <div className="max-w-2xl mx-auto">
                  <div className="bg-white rounded-lg shadow-sm p-6 space-y-6">
                    {/* Title */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Titre <span className="text-gray-700">*</span>
                      </label>
                      <input
                        type="text"
                        name="title"
                        value={translationData.title || ''}
                        onChange={handleChange}
                        className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835] ${
                          errors.title ? 'border-gray-500' : 'border-gray-300'
                        }`}
                        placeholder={`Ex: ${entityData?.type === 'TEXT' ? 'Introduction au concept' : entityData?.type === 'VIDEO' ? 'Vidéo d\'introduction' : 'Titre du bloc'}`}
                        required
                      />
                      {errors.title && (
                        <p className="mt-1 text-sm text-gray-700">{errors.title}</p>
                      )}
                    </div>

                    {/* Description */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Description
                      </label>
                      <textarea
                        name="description"
                        value={translationData.description || ''}
                        onChange={handleChange}
                        rows={2}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
                        placeholder="Description du bloc (optionnel)"
                      />
                    </div>

                    {/* Contenu selon le type de bloc */}
                    {entityData?.type === 'TEXT' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Contenu textuel <span className="text-red-500">*</span>
                        </label>
                        <TipTapEditor
                          key={`${entityId}-${selectedLanguage || initialLanguage}`}
                          content={translationData.content?.html || ''}
                          onChange={handleTextContentChange}
                          placeholder="Saisissez le contenu textuel de votre bloc..."
                        />
                        {errors.content && (
                          <p className="mt-1 text-sm text-red-600">{errors.content}</p>
                        )}
                      </div>
                    )}

                    {entityData?.type === 'IMAGE' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Légende
                        </label>
                        <textarea
                          name="content"
                          value={translationData.content?.caption || ''}
                          onChange={(e) => handleContentChange('caption', e.target.value)}
                          rows={3}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
                          placeholder="Légende de l'image (optionnel)"
                        />
                      </div>
                    )}

                    {entityData?.type === 'VIDEO' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Transcription
                        </label>
                        <textarea
                          name="content"
                          value={translationData.content?.transcript || ''}
                          onChange={(e) => handleContentChange('transcript', e.target.value)}
                          rows={6}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
                          placeholder="Transcription de la vidéo (optionnel)"
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}

               {/* QUIZ FORM - Structure identique à QuizForm */}
               {type === 'quiz' && (selectedLanguage || initialLanguage) && (
                 <div className="max-w-4xl mx-auto space-y-6">
                   {/* Quiz Info Section */}
                   <div className="bg-white rounded-lg shadow-sm p-6 space-y-6">
                     {/* Title */}
                     <div>
                       <label className="block text-sm font-medium text-gray-700 mb-2">
                         Titre <span className="text-red-500">*</span>
                       </label>
                       <input
                         type="text"
                         name="title"
                         value={translationData.title || ''}
                         onChange={handleChange}
                         className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835] ${
                           errors.title ? 'border-red-500' : 'border-gray-300'
                         }`}
                         placeholder="Ex: Quiz de fin de module"
                         required
                       />
                       {errors.title && (
                         <p className="mt-1 text-sm text-red-600">{errors.title}</p>
                       )}
                     </div>

                     {/* Description */}
                     <div>
                       <label className="block text-sm font-medium text-gray-700 mb-2">
                         Description
                       </label>
                       <textarea
                         name="description"
                         value={translationData.description || ''}
                         onChange={handleChange}
                         rows={3}
                         className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
                         placeholder="Description du quiz..."
                       />
                     </div>

                     {/* Instructions */}
                     <div>
                       <label className="block text-sm font-medium text-gray-700 mb-2">
                         Instructions
                       </label>
                       <textarea
                         name="instructions"
                         value={translationData.instructions || ''}
                         onChange={handleChange}
                         rows={2}
                         className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
                         placeholder="Instructions pour les étudiants..."
                       />
                     </div>
                   </div>

                  {/* Questions Section */}
                  <div className="bg-white rounded-lg shadow-sm p-6 space-y-6">
                    <div className="flex items-center justify-between border-b pb-2">
                      <h3 className="text-lg font-semibold text-gray-900">Questions ({questions.length})</h3>
                      {questions.length > 0 && (selectedLanguage === 'fr' || initialLanguage === 'fr') && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={handleCopyAllQuestionsTranslations}
                          disabled={copyingTranslations}
                          className="text-xs"
                        >
                          {copyingTranslations ? (
                            <>
                              <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                              Copie...
                            </>
                          ) : (
                            <>
                              <Copy className="w-3 h-3 mr-1" />
                              Copier vers en
                            </>
                          )}
                        </Button>
                      )}
                    </div>

                    {/* Questions existantes */}
                    {questions.length > 0 && (
                      <div className="space-y-4">
                        {questions.map((q, index) => (
                          <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="text-sm font-medium text-gray-700">Question {index + 1}</span>
                                  <span className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded">
                                    {QUESTION_TYPES.find(t => t.value === q.type)?.label || q.type}
                                  </span>
                                  <span className="text-xs text-gray-500">
                                    {q.points} point{q.points > 1 ? 's' : ''}
                                  </span>
                                </div>
                                <p className="text-gray-900 font-medium mb-2">{q.question}</p>
                                {needsOptions(q.type) && q.options && q.options.length > 0 && (
                                  <div className="space-y-1 mt-2">
                                    {q.options.map((opt, optIndex) => (
                                      <div key={optIndex} className="flex items-center gap-2 text-sm">
                                        <input
                                          type={q.type === 'MULTIPLE_CHOICE' ? 'checkbox' : 'radio'}
                                          checked={opt.isCorrect}
                                          readOnly
                                          className="text-green-600"
                                        />
                                        <span className={opt.isCorrect ? 'text-green-700 font-medium' : 'text-gray-600'}>
                                          {opt.text}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                              <div className="flex items-center gap-1">
                                <button
                                  type="button"
                                  onClick={() => handleOpenQuestionModal(index)}
                                  className="text-blue-600 hover:text-blue-800 p-1.5 rounded transition-colors flex items-center gap-1"
                                  title="Modifier la traduction de la question"
                                >
                                  <Save className="w-4 h-4" />
                                  <span className="text-xs">Modifier</span>
                                </button>
                                {q.id && (
                                  <button
                                    type="button"
                                    onClick={() => handleCopyQuestionTranslations(q.id)}
                                    disabled={copyingTranslations}
                                    className={`p-1.5 rounded transition-colors ${
                                      copyingTranslations && copyingQuestionId === q.id
                                        ? 'text-gray-400 cursor-not-allowed'
                                        : 'text-purple-600 hover:text-purple-800 hover:bg-purple-50'
                                    }`}
                                    title={`Copier les traductions vers ${selectedLanguage === 'fr' || initialLanguage === 'fr' ? 'en' : selectedLanguage === 'en' || initialLanguage === 'en' ? 'fr' : 'l\'autre langue'}`}
                                  >
                                    {copyingTranslations && copyingQuestionId === q.id ? (
                                      <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                      <Copy className="w-4 h-4" />
                                    )}
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                  </div>
                </div>
              )}
              </div>

              {/* Footer avec boutons */}
              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCloseForm}
                  disabled={saving}
                >
                  Annuler
                </Button>
                <Button
                  type="submit"
                  disabled={saving}
                  className="bg-[#141835] hover:bg-[#1a1f4a] text-white"
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sauvegarde...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Sauvegarder la traduction ({SUPPORTED_LANGUAGES.find(l => l.code === (selectedLanguage || initialLanguage))?.name || (selectedLanguage || initialLanguage)?.toUpperCase() || 'N/A'})
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        ) : null}
      </div>

      {/* Modal pour ajouter/éditer une question */}
      {isQuestionModalOpen && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={handleCloseQuestionModal}
        >
          <div 
            className="bg-white rounded-lg shadow-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-gray-900">
                Modifier la traduction de la question
              </h3>
              <button
                onClick={handleCloseQuestionModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Formulaire de question - seulement pour la traduction */}
            <div className="space-y-4">
              {/* Type de question (lecture seule) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Type de question
                </label>
                <input
                  type="text"
                  value={QUESTION_TYPES.find(t => t.value === currentQuestion.type)?.label || currentQuestion.type}
                  readOnly
                  disabled
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed"
                />
              </div>

              {/* Question */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Texte de la question <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="question"
                  value={currentQuestion.question}
                  onChange={handleQuestionChange}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
                  placeholder="Saisissez la traduction de la question..."
                />
              </div>

              {/* Points (lecture seule) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Points
                </label>
                <input
                  type="number"
                  value={currentQuestion.points}
                  readOnly
                  disabled
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed"
                />
              </div>

              {/* Options (si nécessaire) - seulement le texte */}
              {needsOptions(currentQuestion.type) && currentQuestion.options && currentQuestion.options.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Traductions des options
                  </label>
                  <div className="space-y-2">
                    {currentQuestion.options.map((option, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <input
                          type={currentQuestion.type === 'MULTIPLE_CHOICE' ? 'checkbox' : 'radio'}
                          checked={option.isCorrect}
                          readOnly
                          disabled
                          className="text-green-600 cursor-not-allowed"
                        />
                        <input
                          type="text"
                          value={option.text}
                          onChange={(e) => handleOptionChange(index, 'text', e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
                          placeholder={`Traduction de l'option ${index + 1}`}
                        />
                        <span className={`text-xs px-2 py-1 rounded ${
                          option.isCorrect 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          {option.isCorrect ? 'Correcte' : 'Incorrecte'}
                        </span>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Modifiez uniquement les textes des options. Les bonnes réponses ne peuvent pas être modifiées ici.
                  </p>
                </div>
              )}

              {/* Bouton sauvegarder */}
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCloseQuestionModal}
                >
                  Annuler
                </Button>
                <Button
                  type="button"
                  onClick={handleAddQuestion}
                  className="bg-[#141835] hover:bg-[#1a1f4a] text-white"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Sauvegarder la traduction
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
